import { applyRestaurantColors, resetRestaurantColors } from '../theme/colors';

// Preset color themes for different restaurant types
export const restaurantColorThemes = {
  // Italian Restaurant - Warm Reds and Greens
  italian: {
    primary: '#dc2626', // Red
    secondary: '#16a34a', // Green
    accent: '#f59e0b', // Amber
  },
  
  // Japanese Restaurant - Deep Blues and Reds
  japanese: {
    primary: '#1e40af', // Deep Blue
    secondary: '#dc2626', // Red
    accent: '#fbbf24', // Yellow
  },
  
  // Mexican Restaurant - Vibrant Colors
  mexican: {
    primary: '#059669', // Green
    secondary: '#dc2626', // Red
    accent: '#f59e0b', // Yellow
  },
  
  // French Restaurant - Elegant Purples
  french: {
    primary: '#7c3aed', // Purple
    secondary: '#ec4899', // Pink
    accent: '#f59e0b', // Gold
  },
  
  // Chinese Restaurant - Red and Gold
  chinese: {
    primary: '#dc2626', // Red
    secondary: '#f59e0b', // Gold
    accent: '#059669', // Green
  },
  
  // Indian Restaurant - Spicy Colors
  indian: {
    primary: '#ea580c', // Orange
    secondary: '#dc2626', // Red
    accent: '#fbbf24', // Yellow
  },
  
  // Mediterranean Restaurant - Ocean Blues
  mediterranean: {
    primary: '#0891b2', // Cyan
    secondary: '#059669', // Green
    accent: '#f59e0b', // Yellow
  },
  
  // American Restaurant - Classic Blues
  american: {
    primary: '#1e40af', // Blue
    secondary: '#dc2626', // Red
    accent: '#fbbf24', // Yellow
  },
  
  // Thai Restaurant - Tropical Colors
  thai: {
    primary: '#059669', // Green
    secondary: '#dc2626', // Red
    accent: '#fbbf24', // Yellow
  },
  
  // Vegan/Health Food - Earth Tones
  vegan: {
    primary: '#059669', // Green
    secondary: '#d97706', // Orange
    accent: '#fbbf24', // Yellow
  },
};

// Function to apply colors based on cuisine type
export const applyCuisineColors = (cuisineType: string) => {
  const normalizedCuisine = cuisineType.toLowerCase();
  
  // Map cuisine types to themes
  const cuisineMap: Record<string, keyof typeof restaurantColorThemes> = {
    'italian': 'italian',
    'japanese': 'japanese',
    'mexican': 'mexican',
    'french': 'french',
    'chinese': 'chinese',
    'indian': 'indian',
    'mediterranean': 'mediterranean',
    'american': 'american',
    'thai': 'thai',
    'vegan': 'vegan',
    'vegetarian': 'vegan',
    'health food': 'vegan',
    'organic': 'vegan',
  };
  
  // Find matching theme
  const themeKey = Object.keys(cuisineMap).find(key => 
    normalizedCuisine.includes(key)
  );
  
  if (themeKey) {
    const theme = restaurantColorThemes[cuisineMap[themeKey]];
    applyRestaurantColors(theme);
    return theme;
  }
  
  // Default to pastel theme if no match
  resetRestaurantColors();
  return null;
};

// Function to apply custom colors
export const applyCustomRestaurantColors = (colors: {
  primary?: string;
  secondary?: string;
  accent?: string;
}) => {
  applyRestaurantColors(colors);
};

// Function to get a random theme
export const getRandomTheme = () => {
  const themes = Object.keys(restaurantColorThemes);
  const randomTheme = themes[Math.floor(Math.random() * themes.length)];
  return restaurantColorThemes[randomTheme as keyof typeof restaurantColorThemes];
};

// Function to apply random theme
export const applyRandomTheme = () => {
  const theme = getRandomTheme();
  applyRestaurantColors(theme);
  return theme;
};

// Function to reset to default pastel theme
export const resetToDefaultTheme = () => {
  resetRestaurantColors();
};

// Example usage in a component:
/*
import { applyCuisineColors, applyCustomRestaurantColors } from '../utils/restaurantColors';

// In a restaurant details component:
useEffect(() => {
  if (restaurant?.cuisine_type) {
    applyCuisineColors(restaurant.cuisine_type);
  }
  
  // Cleanup when component unmounts
  return () => {
    resetToDefaultTheme();
  };
}, [restaurant?.cuisine_type]);

// Or apply custom colors:
applyCustomRestaurantColors({
  primary: '#ff6b6b',
  secondary: '#4ecdc4',
  accent: '#45b7d1'
});
*/
